// Sticker labels for invitation cards: the sheet geometry, the blocks printed
// inside one label, and the PDF that comes out. Everything is in MILLIMETRES —
// a sticker sheet is a physical object, and mm is what its box quotes.
//
// A template is one plain object (saved per event), so it can be stored
// anywhere: the API when it is reachable, this browser otherwise.

export const A4 = { width: 210, height: 297 };

// The sheets sold in stationery shops here, by labels per sheet.
export const SHEET_PRESETS = [
  {
    key: '65', label: '65 per sheet · 38.1 × 21.2 mm (3 × 8 … Avery L7651)',
    sheet: { labelWidth: 38.1, labelHeight: 21.2, marginTop: 10.7, marginLeft: 4.7, gapX: 2.5, gapY: 0, columns: 5, rows: 13 },
  },
  {
    key: '24', label: '24 per sheet · 63.5 × 33.9 mm (3 × 8 … Avery L7159)',
    sheet: { labelWidth: 63.5, labelHeight: 33.9, marginTop: 13.1, marginLeft: 7.2, gapX: 2.5, gapY: 0, columns: 3, rows: 8 },
  },
  {
    key: '21', label: '21 per sheet · 63.5 × 38.1 mm (3 × 7 … Avery L7160)',
    sheet: { labelWidth: 63.5, labelHeight: 38.1, marginTop: 15.1, marginLeft: 7.2, gapX: 2.5, gapY: 0, columns: 3, rows: 7 },
  },
  {
    key: '12', label: '12 per sheet · 63.5 × 72 mm (2 × 6)',
    sheet: { labelWidth: 63.5, labelHeight: 72, marginTop: 11, marginLeft: 36, gapX: 11, gapY: 0, columns: 2, rows: 4 },
  },
  {
    key: '10', label: '10 per sheet · 99.1 × 57 mm (2 × 5 … Avery L7173)',
    sheet: { labelWidth: 99.1, labelHeight: 57, marginTop: 4.5, marginLeft: 4.65, gapX: 2.5, gapY: 0, columns: 2, rows: 5 },
  },
  {
    key: '8', label: '8 per sheet · 99.1 × 67.7 mm (2 × 4 … Avery L7165)',
    sheet: { labelWidth: 99.1, labelHeight: 67.7, marginTop: 13.1, marginLeft: 4.65, gapX: 2.5, gapY: 0, columns: 2, rows: 4 },
  },
];

// What a block can print. `sample` is what the designer shows while nothing
// real is loaded; `value(card, ctx)` is what a sheet actually prints.
export const LABEL_FIELDS = [
  { key: 'name', label: 'Card name', sample: 'Ailani Kumar Uttamchand', value: (c) => c.name || '' },
  { key: 'firm', label: 'Firm / Company', sample: 'Sharma Traders', value: (c) => (String(c.type) === 'Business' ? (c.subtitle || '') : '') },
  { key: 'city', label: 'City', sample: 'Ulhasnagar', value: (c) => c.city || '' },
  { key: 'mobile', label: 'Mobile', sample: '98222 10195', value: (c) => c.mobile || '' },
  { key: 'persons', label: 'Family of N', sample: 'Family of 4', value: (c) => (c.persons > 1 ? `Family of ${c.persons}` : '') },
  { key: 'functions', label: 'Functions invited to', sample: 'Wedding, Reception', value: (c) => (c.functions || []).join(', ') },
  { key: 'event', label: 'Event name', sample: 'Sahil weds Diksha', value: (c, ctx) => ctx.eventName || '' },
  { key: 'text', label: 'Fixed text', sample: 'With best compliments', value: (c, ctx, block) => block.text || '' },
  { key: 'line', label: 'Divider line', sample: '', value: () => '' },
];

export const fieldDef = (key) => LABEL_FIELDS.find(f => f.key === key) || LABEL_FIELDS[0];

// A sensible first label: the name, the firm under it, then the city.
export const defaultBlocks = () => ([
  { id: 'b1', field: 'name', x: 3, y: 4, w: 32, size: 11, bold: true, align: 'left' },
  { id: 'b2', field: 'firm', x: 3, y: 10, w: 32, size: 8, bold: false, align: 'left' },
  { id: 'b3', field: 'city', x: 3, y: 15, w: 32, size: 8, bold: false, align: 'left' },
]);

export const defaultTemplate = () => ({
  name: 'Invitation labels',
  preset: '65',
  sheet: { ...SHEET_PRESETS[0].sheet },
  offsetX: 0,          // printer drift, ± mm
  offsetY: 0,
  padding: 2,
  outline: false,      // print a hairline round each label (helps when cutting)
  blocks: defaultBlocks(),
});

// Where every label of a sheet sits, in mm from the top-left of the paper.
export function labelPositions(sheet, offsetX = 0, offsetY = 0) {
  const out = [];
  for (let r = 0; r < Number(sheet.rows || 0); r += 1) {
    for (let c = 0; c < Number(sheet.columns || 0); c += 1) {
      out.push({
        row: r,
        col: c,
        x: Number(sheet.marginLeft || 0) + c * (Number(sheet.labelWidth || 0) + Number(sheet.gapX || 0)) + Number(offsetX || 0),
        y: Number(sheet.marginTop || 0) + r * (Number(sheet.labelHeight || 0) + Number(sheet.gapY || 0)) + Number(offsetY || 0),
      });
    }
  }
  return out;
}

// Does the grid actually fit an A4 page? The setup page shows this in words.
export function sheetProblems(sheet) {
  const problems = [];
  const w = Number(sheet.marginLeft || 0) + Number(sheet.columns || 0) * Number(sheet.labelWidth || 0)
    + Math.max(Number(sheet.columns || 0) - 1, 0) * Number(sheet.gapX || 0);
  const h = Number(sheet.marginTop || 0) + Number(sheet.rows || 0) * Number(sheet.labelHeight || 0)
    + Math.max(Number(sheet.rows || 0) - 1, 0) * Number(sheet.gapY || 0);
  if (w > A4.width + 0.5) problems.push(`The columns need ${w.toFixed(1)} mm — wider than A4 (${A4.width} mm).`);
  if (h > A4.height + 0.5) problems.push(`The rows need ${h.toFixed(1)} mm — taller than A4 (${A4.height} mm).`);
  if (!Number(sheet.columns) || !Number(sheet.rows)) problems.push('Columns and rows must both be at least 1.');
  if (!Number(sheet.labelWidth) || !Number(sheet.labelHeight)) problems.push('A label needs a width and a height.');
  return problems;
}

export const perSheet = (sheet) => Math.max(Number(sheet.columns || 0) * Number(sheet.rows || 0), 0);

// The lines one card prints, block by block (empty ones are skipped).
export function labelLines(card, ctx, blocks) {
  return blocks.map(b => ({ ...b, text: String(fieldDef(b.field).value(card, ctx, b) || '') }))
    .filter(b => b.field === 'line' || b.text);
}

// ── The sheets themselves ───────────────────────────────────────────────────
// jsPDF, in mm, one page per sheet. `startAt` skips labels already peeled off
// a part-used sheet; a card with extra cards prints that many more labels.
export async function buildLabelsPdf({ template, cards, eventName, startAt = 1, withExtras = true }) {
  const { default: JsPDF } = await import('jspdf');
  const doc = new JsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const sheet = template.sheet;
  const slots = labelPositions(sheet, template.offsetX, template.offsetY);
  const pad = Number(template.padding || 0);

  // One entry per sticker to print.
  const queue = [];
  cards.forEach(card => {
    const copies = 1 + (withExtras ? Math.max(Number(card.extraCards || 0), 0) : 0);
    for (let i = 0; i < copies; i += 1) queue.push(card);
  });

  let slot = Math.max(Number(startAt || 1), 1) - 1;   // 0-based
  let page = 0;
  queue.forEach((card, i) => {
    if (slot >= slots.length) { slot = 0; page += 1; }
    if (i === 0 || slot === 0) { if (page > 0 && slot === 0) doc.addPage(); }
    const pos = slots[slot];
    if (template.outline) {
      doc.setDrawColor(190); doc.setLineWidth(0.1);
      doc.rect(pos.x, pos.y, sheet.labelWidth, sheet.labelHeight);
    }
    labelLines(card, { eventName }, template.blocks).forEach(b => {
      const x = pos.x + pad + Number(b.x || 0);
      const y = pos.y + pad + Number(b.y || 0);
      if (b.field === 'line') {
        doc.setDrawColor(120); doc.setLineWidth(0.2);
        doc.line(x, y, x + Number(b.w || 20), y);
        return;
      }
      doc.setFont('helvetica', b.bold ? 'bold' : 'normal');
      doc.setFontSize(Number(b.size || 9));
      doc.setTextColor(20);
      const maxW = Math.max(Number(b.w || 30), 5);
      const lines = doc.splitTextToSize(b.text, maxW);
      const align = b.align === 'center' ? 'center' : b.align === 'right' ? 'right' : 'left';
      const anchorX = align === 'center' ? x + maxW / 2 : align === 'right' ? x + maxW : x;
      doc.text(lines, anchorX, y, { align, baseline: 'top' });
    });
    slot += 1;
  });

  return doc.output('blob');
}
